import { useCallback, useState } from 'react';
import { cn } from '../lib/utils.js';
import { useTheme } from '../lib/theme.js';

/**
 * Sandbox flags: NO `allow-scripts` (nothing in the HTML can run). `allow-same-origin` only lets the
 * parent measure the content height; links open in a new tab (`allow-popups…`).
 */
export const HTML_FRAME_SANDBOX = 'allow-same-origin allow-popups allow-popups-to-escape-sandbox';

const CSP = "default-src 'none'; img-src https: data: cid:; style-src 'unsafe-inline'; font-src https: data:";

const LIGHT_STYLE =
  'body{margin:12px;font:14px/1.5 system-ui,sans-serif;color:#1f2937;background:#fff;word-wrap:break-word}img{max-width:100%;height:auto}table{max-width:100%}';

// Dunkel: Fremd-HTML übernimmt Text-/Hintergrundfarben nicht (sonst schwarzer Text auf dunklem Grund)
const DARK_STYLE =
  'html{color-scheme:dark}body{margin:12px;font:14px/1.5 system-ui,sans-serif;color:#e4e4e7;background:transparent;word-wrap:break-word}' +
  'body *{color:inherit!important;background-color:transparent!important;border-color:#3f3f46!important}a,a *{color:#f7a654!important}' +
  'img{max-width:100%;height:auto}table{max-width:100%}';

/**
 * Builds the srcdoc: strict CSP (no scripts, no external requests except images), links open in a new tab.
 * @param {string} html
 * @param {'light'|'dark'} [scheme]
 */
export function buildSrcDoc(html, scheme = 'light') {
  return `<!doctype html><html lang="de"><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${CSP}"><base target="_blank"><style>${scheme === 'dark' ? DARK_STYLE : LIGHT_STYLE}</style></head><body>${html ?? ''}</body></html>`;
}

/**
 * Renders untrusted/foreign HTML (e-mail bodies, rich text) isolated in a sandboxed iframe via `srcdoc`.
 * The height follows the content (capped by `maxHeight`).
 * `colorScheme="auto"` follows the app theme (for rich text such as announcements); the default `light` keeps the
 * original look (e-mail previews must show what recipients see).
 * @param {{ html: string, title: string, className?: string, minHeight?: number, maxHeight?: number, colorScheme?: 'light'|'auto' }} props
 */
export function HtmlFrame({ html, title, className, minHeight = 160, maxHeight = 900, colorScheme = 'light' }) {
  const [height, setHeight] = useState(minHeight);
  const { resolvedTheme } = useTheme();
  const scheme = colorScheme === 'auto' && resolvedTheme === 'dark' ? 'dark' : 'light';
  const onLoad = useCallback(
    (/** @type {import('react').SyntheticEvent<HTMLIFrameElement>} */ event) => {
      try {
        const doc = event.currentTarget.contentDocument;
        const content = doc?.documentElement?.scrollHeight ?? 0;
        if (content > 0) setHeight(Math.min(Math.max(content + 4, minHeight), maxHeight));
      } catch {
        // not measurable – keep the minimum height
      }
    },
    [minHeight, maxHeight],
  );
  return (
    <iframe
      title={title}
      sandbox={HTML_FRAME_SANDBOX}
      referrerPolicy="no-referrer"
      srcDoc={buildSrcDoc(html, scheme)}
      onLoad={onLoad}
      style={{ height }}
      className={cn('block w-full rounded-md border', scheme === 'dark' ? 'bg-transparent' : 'bg-white', className)}
    />
  );
}
